<script setup lang="ts">
  import { AuditLog } from '@/common/graphql/types'
  import { computed, PropType } from 'vue'
  import MdiIcon from '@/common/components/svg/MdiIcon.vue'
  import { mdiAccount, mdiDatabase, mdiLightningBolt, mdiRecycle, mdiImport, mdiExport } from '@mdi/js'
  import AuditLogUser from '@/common/components/auditlog/components/AuditLogUser.vue'
  import { parseISO, formatDate } from '@/common/services/time'

  const props = defineProps({
    log: {
      type: Object as PropType<AuditLog>,
      required: true
    },
  })

  const metaData = computed(() => {
    if (!props.log.meta) {
      return {}
    }

    try {
      return JSON.parse(props.log.meta)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return {}
    }
  })

  const meta = computed(() => {
    switch (props.log.action) {
    case 'automatic_cleanup':
      return { until: formatDate(parseISO(metaData.value.cleaned_until)) }
    case 'gets_imported':
      return { count: Number(metaData.value) }
    default:
      return {}
    }
  })

  const icon = computed(() => {
    switch (props.log.action) {
    case 'backup':
      return mdiDatabase
    case 'loggedin':
      return mdiAccount
    case 'automatic_cleanup':
      return mdiRecycle
    case 'gets_committed':
      return mdiExport
    case 'gets_imported':
      return mdiImport
    case 'created':
      if (props.log.entity_type === 'configbackup') {
        return mdiDatabase
      }
    // fallthrough
    default:
      return mdiLightningBolt
    }
  })
</script>

<template>
  <div class="flex-1 flex items-center text-gray-600 relative min-h-12">
    <div class="h-px w-full bg-gray-200 flex items-center justify-center rounded-full">
      <div class="bg-gray-50 text-sm px-2 py-1 flex items-center gap-2">
        <div>
          <MdiIcon :icon="icon" class="size-4" />
        </div>
        <div class="text-center text-xs">
          {{ $t('auditlog.actions.' + props.log.action, meta) }}
        </div>
      </div>
    </div>
    <div v-if="log.user" class="pr-0.5 absolute z-10 right-0 bg-gray-50">
      <AuditLogUser class="pl-4" :user="log.user" />
    </div>
  </div>
</template>